import type * as React from 'react'
import type { ActivityEntry, ActivityFact, ActivityGroup } from '@/components/visitone/activity-history'
import type { TableCell } from '@/components/visitone/server-data-table'
import type {
  GuestActivity,
  GuestChannel,
  GuestDiagnostic,
  GuestHistoryItem,
  GuestHistoryVisit,
  GuestMembership,
  GuestMessageKind,
  GuestName,
  GuestNotice,
} from '@/lib/bff/guests'
import {
  formatCalendarDate,
  formatLocalDateTime,
  formatLocalTime,
  formatMonthHeading,
  formatTenantDateTime,
  formatTenantInstant,
  monthKeyOf,
  tenantDayOf,
  toTenantLocal,
} from '@/lib/format/tenant-date'
import type { Translator } from '@/lib/i18n'

/* TECH-DEBT(VIS-327): lists and amounts in en-GB, as the dates are, until the tenant formatter exists. */
const listFormat = new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' })
const amountFormat = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export const guestName = (t: Translator, name: GuestName) =>
  t('guests.personName', { firstName: name.firstName, lastName: name.lastName }).trim()

/** The Name cell: the name, and the email underneath only when the BFF sent it. */
export function nameCell(t: Translator, name: GuestName, email?: string): TableCell {
  return { primary: guestName(t, name), secondary: email }
}

export function membershipCell(t: Translator, memberships: GuestMembership[]): TableCell {
  if (memberships.length === 0) return { primary: t('guests.row.noMembership') }
  return {
    primary: listFormat.format(memberships.map((membership) => membership.name)),
    secondary:
      memberships.length === 1 ? t('guests.row.membershipUntil', { date: formatCalendarDate(memberships[0].endDate) }) : undefined,
  }
}

/** A visit's performance in a list row: title, then when and where. */
export function activityCell(t: Translator, activity: GuestActivity | undefined): TableCell {
  if (!activity) return { primary: '' }
  const when = activity.at ? formatLocalDateTime(activity.at) : undefined
  const secondary = when && activity.venueName ? t('guests.row.visitWhere', { when, venue: activity.venueName }) : (when ?? activity.venueName)
  return { primary: activity.title ?? t('guests.row.visit'), secondary }
}

/** Arrivals today: the time and title, then the venue. */
export function visitTodayCell(t: Translator, activity: GuestActivity | undefined): TableCell {
  if (!activity) return { primary: '' }
  const time = activity.at ? formatLocalTime(activity.at) : undefined
  const title = activity.title ?? t('guests.row.visit')
  return { primary: time ? t('guests.row.visitAt', { time, title }) : title, secondary: activity.venueName }
}

export const messageTitle = (t: Translator, channel: GuestChannel | undefined, kind: GuestMessageKind | undefined) =>
  t(`guests.history.message.${channel === 'email' ? 'email' : 'sms'}.${kind ?? 'other'}`)

export const formatInstant = (iso: string | undefined, timeZone: string) => (iso ? formatTenantInstant(iso, timeZone) : '')

export type HistoryFilter = 'all' | 'visits' | 'orders' | 'tables' | 'messages' | 'moments'
export const HISTORY_FILTERS: HistoryFilter[] = ['all', 'visits', 'orders', 'tables', 'messages', 'moments']

function itemMatches(item: GuestHistoryItem, filter: HistoryFilter) {
  switch (filter) {
    case 'orders':
      return item.type === 'order'
    case 'tables':
      return item.type === 'table'
    case 'messages':
      return (item.type === 'message' && item.kind !== 'post_show_moment') || item.type === 'not_sent'
    case 'moments':
      return item.type === 'message' && item.kind === 'post_show_moment'
    default:
      return true
  }
}

export type HistoryIcons = {
  visit: React.ReactNode
  sms: React.ReactNode
  email: React.ReactNode
  moment: React.ReactNode
  table: React.ReactNode
  order: React.ReactNode
}

export type HistoryContext = {
  t: Translator
  timeZone: string
  /** The guest whose record this is, so their own party entry says "(this guest)". */
  guestId: string
  /** Shows a party member: a link to their record when they have one, otherwise their name. */
  renderGuest: (member: { guestId?: string; name: string }) => React.ReactNode
  icons: HistoryIcons
  now?: Date
}

/** When an instant or tenant-local time is shown on the history: "Today, 09:00" today, else the full date. */
function whenOf(ctx: HistoryContext, local: string | null | undefined) {
  if (!local) return undefined
  return tenantDayOf(local, ctx.timeZone, ctx.now) === 'other'
    ? formatLocalDateTime(local)
    : ctx.t('guests.history.todayAt', { time: formatLocalTime(local) })
}

const instantLocal = (ctx: HistoryContext, iso: string | undefined) => (iso ? toTenantLocal(iso, ctx.timeZone) : null)

function diagnosticFacts(ctx: HistoryContext, diagnostic: GuestDiagnostic | undefined, at: string | undefined): ActivityFact[] {
  if (!diagnostic) return []
  const { t, timeZone } = ctx
  const facts: ActivityFact[] = []
  if (at) facts.push({ label: t('guests.history.diagnostic.time'), value: formatTenantDateTime(at, timeZone) })
  if (diagnostic.status) facts.push({ label: t('guests.history.diagnostic.status'), value: diagnostic.status })
  if (diagnostic.stage) facts.push({ label: t('guests.history.diagnostic.stage'), value: diagnostic.stage })
  if (diagnostic.providerMessageId) facts.push({ label: t('guests.history.diagnostic.messageId'), value: diagnostic.providerMessageId })
  if (diagnostic.attempts?.length) {
    facts.push({
      label: t('guests.history.diagnostic.attempts'),
      value: diagnostic.attempts.map((attempt, index) => {
        const time = attempt.at ? formatTenantDateTime(attempt.at, timeZone) : ''
        return (
          <span key={index} className="block">
            {attempt.status
              ? t('guests.history.diagnostic.attempt', { status: attempt.status, time })
              : t('guests.history.diagnostic.attemptNoStatus', { time })}
          </span>
        )
      }),
    })
  }
  if (diagnostic.entryType) facts.push({ label: t('guests.history.diagnostic.entryType'), value: diagnostic.entryType })
  if (diagnostic.batchId) facts.push({ label: t('guests.history.diagnostic.batchId'), value: diagnostic.batchId })
  if (diagnostic.orderId) facts.push({ label: t('guests.history.diagnostic.orderId'), value: diagnostic.orderId })
  if (diagnostic.text) facts.push({ label: t('guests.history.diagnostic.text'), value: diagnostic.text })
  return facts
}

function messageEntry(ctx: HistoryContext, item: GuestHistoryItem, id: string): ActivityEntry {
  const { t, icons } = ctx
  const moment = item.kind === 'post_show_moment'
  let status: ActivityEntry['status']
  let description: string | undefined
  if (item.outcome === 'delivered') status = { tone: 'success', label: t('guests.history.message.delivered') }
  else if (item.outcome === 'failed' && item.resolvedLater) {
    status = { tone: 'neutral', label: t('guests.history.message.deliveredLater') }
    description = t('guests.history.message.deliveredLaterBody')
  } else if (item.outcome === 'failed') {
    status = { tone: 'danger', label: t('guests.history.message.notDelivered') }
    description = t('guests.history.message.failedBody')
  } else {
    status = { tone: 'neutral', label: t('guests.history.message.sent') }
    description = t('guests.history.message.sentBody')
  }
  return {
    id,
    icon: moment ? icons.moment : item.channel === 'email' ? icons.email : icons.sms,
    title: messageTitle(t, item.channel, item.kind),
    when: whenOf(ctx, instantLocal(ctx, item.at)),
    status,
    description,
    diagnostic: diagnosticFacts(ctx, item.diagnostic, item.at),
  }
}

function notSentEntry(ctx: HistoryContext, item: GuestHistoryItem, id: string): ActivityEntry {
  const { t, icons } = ctx
  const couldNotSend = item.outcome === 'could_not_send'
  const description = item.reason
    ? t(`guests.history.notSent.reason.${item.reason}`)
    : t(couldNotSend ? 'guests.history.notSent.genericCouldNotSend' : 'guests.history.notSent.generic')
  return {
    id,
    icon: item.channel === 'email' ? icons.email : icons.sms,
    title: t(`guests.history.notSent.${item.channel === 'email' ? 'email' : 'sms'}`),
    when: whenOf(ctx, instantLocal(ctx, item.at)),
    status: couldNotSend
      ? { tone: 'warning', label: t('guests.history.notSent.couldNotSend') }
      : { tone: 'neutral', label: t('guests.history.notSent.notSent') },
    description,
    diagnostic: diagnosticFacts(ctx, item.diagnostic, item.at),
  }
}

function tableEntry(ctx: HistoryContext, item: GuestHistoryItem, id: string): ActivityEntry {
  const { t, icons } = ctx
  const detail = [
    item.partySize !== undefined ? t('guests.history.table.partySize', { count: item.partySize }) : null,
    item.bookingReference ? t('guests.history.table.reference', { reference: item.bookingReference }) : null,
  ].filter((part): part is string => Boolean(part))
  return {
    id,
    icon: icons.table,
    title: t('guests.history.table.title'),
    when: whenOf(ctx, item.at),
    detail: detail.length ? detail.join(' · ') : undefined,
    status: item.cancelled
      ? { tone: 'neutral', label: t('guests.history.table.cancelled') }
      : { tone: 'success', label: t('guests.history.table.confirmed') },
  }
}

const ORDER_STATUS: Record<string, { tone: 'success' | 'neutral'; key: string }> = {
  CLOSED: { tone: 'success', key: 'guests.history.order.paid' },
  PENDING: { tone: 'neutral', key: 'guests.history.order.pending' },
}

function orderEntry(ctx: HistoryContext, item: GuestHistoryItem, id: string): ActivityEntry {
  const { t, icons } = ctx
  const status = item.status ? ORDER_STATUS[item.status] : undefined
  const detail = [
    item.itemCount !== undefined ? t('guests.history.order.items', { count: item.itemCount }) : null,
    item.total !== undefined ? t('guests.history.order.total', { total: amountFormat.format(item.total) }) : null,
    item.locationName ? t('guests.history.order.at', { location: item.locationName }) : null,
  ].filter((part): part is string => Boolean(part))
  return {
    id,
    icon: icons.order,
    title: item.orderTypeName
      ? t('guests.history.order.titleType', { type: item.orderTypeName })
      : t('guests.history.order.title'),
    when: whenOf(ctx, instantLocal(ctx, item.at)),
    detail: detail.length ? detail.join(' · ') : undefined,
    status: status ? { tone: status.tone, label: t(status.key) } : undefined,
    diagnostic: diagnosticFacts(ctx, item.diagnostic, undefined),
  }
}

function itemEntry(ctx: HistoryContext, item: GuestHistoryItem, id: string): ActivityEntry {
  switch (item.type) {
    case 'message':
      return messageEntry(ctx, item, id)
    case 'not_sent':
      return notSentEntry(ctx, item, id)
    case 'table':
      return tableEntry(ctx, item, id)
    default:
      return orderEntry(ctx, item, id)
  }
}

function partyFacts(ctx: HistoryContext, visit: GuestHistoryVisit): ActivityFact[] {
  const { t, guestId, renderGuest } = ctx
  const person = (member: GuestHistoryVisit['party'][number]) => {
    const name = guestName(t, member.name)
    const self = member.guestId === guestId || (member.role === 'booker' && visit.role === 'booker' && !member.guestId)
    return self ? t('guests.thisGuest', { name }) : renderGuest({ guestId: member.guestId, name })
  }
  const list = (members: GuestHistoryVisit['party'], plain = false) =>
    members.map((member, index) => (
      <span key={`${member.guestId ?? 'none'}-${index}`} className="mr-3 inline-block">
        {plain ? guestName(t, member.name) : person(member)}
      </span>
    ))
  const facts: ActivityFact[] = []
  const bookers = visit.party.filter((member) => member.role === 'booker')
  const shadows = visit.party.filter((member) => member.role === 'shadow_guest')
  const pending = visit.party.filter((member) => member.role === 'share_not_accepted')
  if (bookers.length) facts.push({ label: t('guests.history.visit.booker'), value: list(bookers) })
  if (shadows.length) facts.push({ label: t('guests.history.visit.shadowGuests'), value: list(shadows) })
  if (pending.length) facts.push({ label: t('guests.history.visit.shareNotAccepted'), value: list(pending, true) })
  return facts
}

/**
 * The history's groups: Later today and Earlier today for today's visits,
 * and a group per month for the others, in the BFF's order (newest
 * performance first, so visits still to come in this month head the list
 * under its own heading, above today). Only neighbours merge. A visit
 * keeps its own items, oldest first; the filter keeps visits holding a
 * matching item, with only those items (Visits keeps every visit, without
 * items). Groups that end up empty are left out.
 */
export function historyGroups(ctx: HistoryContext, visits: GuestHistoryVisit[], filter: HistoryFilter): ActivityGroup[] {
  const { t, timeZone } = ctx
  const groups: ActivityGroup[] = []
  for (const visit of visits) {
    const items = filter === 'visits' ? [] : visit.items.filter((item) => itemMatches(item, filter))
    if (filter !== 'all' && filter !== 'visits' && items.length === 0) continue

    const at = visit.performance.at
    const day = at ? tenantDayOf(at, timeZone, ctx.now) : 'other'
    const notes = [
      ...(visit.party.length === 0 ? [t('guests.history.visit.noParty')] : []),
      ...(visit.unavailable.includes('orders') ? [t('guests.unavailable.ordersVisit')] : []),
    ]
    const entry: ActivityEntry = {
      id: visit.visitId,
      icon: ctx.icons.visit,
      title: visit.performance.title
        ? t('guests.history.visit.title', { title: visit.performance.title })
        : t('guests.history.visit.untitled'),
      when: whenOf(ctx, at),
      detail: visit.performance.venueName,
      status: day === 'other' ? undefined : { tone: 'info', label: t('guests.history.visit.today') },
      facts: partyFacts(ctx, visit),
      notes,
      items: items.map((item, index) => itemEntry(ctx, item, `${visit.visitId}-${index}`)),
    }

    let key: string
    let heading: string
    if (!at) {
      key = 'undated'
      heading = t('guests.history.undated')
    } else if (day !== 'other') {
      key = day
      heading = t(day === 'later-today' ? 'guests.history.laterToday' : 'guests.history.earlierToday')
    } else {
      key = `month-${monthKeyOf(at)}`
      heading = formatMonthHeading(at)
    }
    const last = groups.at(-1)
    if (last && last.id.split('#')[0] === key) last.entries.push(entry)
    else groups.push({ id: `${key}#${groups.length}`, heading, entries: [entry] })
  }
  return groups
}

/** The record's "Some activity isn't shown" lines, at most one per kind of notice. */
export function notShownLines(t: Translator, notices: GuestNotice[]): string[] {
  const lines: string[] = []
  if (notices.some((notice) => notice === 'partial_association' || notice === 'unattributed_messages')) {
    lines.push(t('guests.history.notShownBody'))
  }
  if (notices.includes('history_limited')) lines.push(t('guests.history.limitedBody'))
  return lines
}
